/**
 * Which sites one contact covers (Phase 17 / Task 17.3, DEC-146 §4).
 *
 * Replace-all editing of a contact's wells and tank batteries, saved with one PUT. An
 * empty list means "covers every site of this customer" — said in words, because an
 * empty list otherwise reads as "covers nothing".
 *
 * Options are the CUSTOMER's own sites: its tank batteries, and wells searched under its
 * platform operator. The server is the authority (it also accepts wells of a company that
 * was merged into this one, and refuses anything else with `not_owned`, which is shown on
 * the offending row). A hauler-private customer has no catalog wells.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { WELL_SEARCH_MAX_LIMIT } from '@lodex-pro/shared/wells-paging';
import { useTankBatteries, useWellSearch, type HaulerOperator } from '../sites-operators/hooks';
import { useDebounced } from '../sites-operators/CatalogSearchModal';
import {
  LoadEmailRequestError,
  useReplaceAssignments,
  type AssignmentInput,
  type AssignmentType,
  type SiteContact,
} from './api';
import {
  buttonStyle,
  errorText,
  helpText,
  inputStyle,
  labelStyle,
  primaryButtonStyle,
} from './styles';

interface Row extends AssignmentInput {
  readonly name: string;
}

const typeLabel = (t: AssignmentType): string => (t === 'WELL' ? 'Well' : 'Tank battery');

export function AssignmentsEditor({
  customer,
  contact,
  canManage,
}: {
  readonly customer: HaulerOperator;
  readonly contact: SiteContact;
  readonly canManage: boolean;
}): React.JSX.Element {
  const initial = React.useMemo<Row[]>(
    () =>
      contact.assignments.map((a) => ({
        type: a.type,
        targetId: a.targetId,
        receiveNotifications: a.receiveNotifications,
        name: a.name ?? a.targetId,
      })),
    [contact.assignments],
  );
  const [rows, setRows] = React.useState<Row[]>(initial);
  const [problemIndexes, setProblemIndexes] = React.useState<ReadonlySet<number>>(new Set());
  React.useEffect(() => {
    setRows(initial);
    setProblemIndexes(new Set());
  }, [initial]);

  const replace = useReplaceAssignments(customer.id);
  const batteries = useTankBatteries();
  const [needle, setNeedle] = React.useState('');
  const debounced = useDebounced(needle, 250);
  const platformOperatorId = customer.operatorId ?? undefined;
  const wells = useWellSearch(
    platformOperatorId === undefined ? '' : debounced,
    platformOperatorId,
  );

  const has = (type: AssignmentType, id: string): boolean =>
    rows.some((r) => r.type === type && r.targetId === id);
  const add = (row: Row): void => {
    if (!has(row.type, row.targetId)) setRows((r) => [...r, row]);
  };
  const ownBatteries = (batteries.data?.tankBatteries ?? []).filter(
    (b) => b.haulerOperatorId === customer.id && b.isActive && !has('TANK_BATTERY', b.id),
  );
  const wellResults = (wells.data?.wells ?? []).filter((w) => !has('WELL', w.id));
  const truncated = (wells.data?.wells.length ?? 0) >= WELL_SEARCH_MAX_LIMIT;
  const dirty = JSON.stringify(rows) !== JSON.stringify(initial);

  const save = (): void => {
    replace.mutate(
      {
        contactId: contact.id,
        payload: {
          assignments: rows.map(({ type, targetId, receiveNotifications }) => ({
            type,
            targetId,
            receiveNotifications,
          })),
        },
      },
      {
        onSuccess: () => {
          setProblemIndexes(new Set());
          toast.success('Sites saved');
        },
        onError: (err) => {
          const bad = new Set<number>();
          if (err instanceof LoadEmailRequestError) {
            for (const f of err.fields) {
              const m = /^assignments\.(\d+)\.targetId$/.exec(f.path);
              if (m !== null && f.reason === 'not_owned') bad.add(Number(m[1]));
            }
          }
          setProblemIndexes(bad);
          toast.error(
            bad.size > 0 ? 'Some sites do not belong to this customer' : 'Could not save sites',
          );
        },
      },
    );
  };

  return (
    <div>
      <h4 style={{ fontSize: '16px', margin: '0 0 8px' }}>Sites</h4>
      {rows.length === 0 ? (
        <p style={helpText} data-testid="covers-all">
          Covers every site of this customer. Add sites below to limit it.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
          {rows.map((r, i) => (
            <li
              key={`${r.type}:${r.targetId}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 0',
                borderBottom: '1px solid var(--color-border-secondary)',
              }}
            >
              <span style={{ flex: 1, fontSize: '16px' }}>
                {r.name}{' '}
                <span style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                  · {typeLabel(r.type)}
                </span>
                {problemIndexes.has(i) && (
                  <span role="alert" style={{ ...errorText, display: 'block', margin: 0 }}>
                    Not one of this customer&apos;s sites.
                  </span>
                )}
              </span>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  minHeight: '56px',
                }}
              >
                <input
                  type="checkbox"
                  style={{ width: '24px', height: '24px' }}
                  checked={r.receiveNotifications}
                  disabled={!canManage}
                  onChange={(e): void =>
                    setRows((all) =>
                      all.map((x, j) =>
                        j === i ? { ...x, receiveNotifications: e.target.checked } : x,
                      ),
                    )
                  }
                />
                Emails
              </label>
              {canManage && (
                <button
                  type="button"
                  style={buttonStyle}
                  aria-label={`Remove ${r.name}`}
                  onClick={(): void => setRows((all) => all.filter((_x, j) => j !== i))}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
          <div>
            <label htmlFor="add-battery" style={labelStyle}>
              Add a tank battery
            </label>
            <select
              id="add-battery"
              style={inputStyle}
              value=""
              onChange={(e): void => {
                const b = ownBatteries.find((x) => x.id === e.target.value);
                if (b !== undefined) {
                  add({
                    type: 'TANK_BATTERY',
                    targetId: b.id,
                    name: b.name,
                    receiveNotifications: true,
                  });
                }
              }}
            >
              <option value="">
                {ownBatteries.length === 0 ? 'No more tank batteries' : 'Choose…'}
              </option>
              {ownBatteries.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          {platformOperatorId === undefined ? (
            <p style={helpText}>
              This customer is not in the lodeX Pro catalog, so it has no catalog wells.
            </p>
          ) : (
            <div>
              <label htmlFor="well-search" style={labelStyle}>
                Add a well
              </label>
              <input
                id="well-search"
                style={inputStyle}
                placeholder="Type 2+ letters of the well name or API number…"
                value={needle}
                onChange={(e): void => setNeedle(e.target.value)}
              />
              {wellResults.length > 0 && (
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '8px 0 0',
                    maxHeight: '280px',
                    overflowY: 'auto',
                  }}
                >
                  {wellResults.map((w) => (
                    <li key={w.id}>
                      <button
                        type="button"
                        style={{
                          ...buttonStyle,
                          width: '100%',
                          textAlign: 'left',
                          fontWeight: 400,
                        }}
                        onClick={(): void =>
                          add({
                            type: 'WELL',
                            targetId: w.id,
                            name: w.wellName,
                            receiveNotifications: true,
                          })
                        }
                      >
                        {w.wellName}{' '}
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          · API {w.apiNumber}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {truncated && (
                <p style={helpText}>
                  Showing the first {WELL_SEARCH_MAX_LIMIT} matching wells — there may be more. Type
                  more of the name to narrow it.
                </p>
              )}
            </div>
          )}
          <div>
            <button
              type="button"
              style={primaryButtonStyle}
              disabled={!dirty || replace.isPending}
              onClick={save}
            >
              {replace.isPending ? 'Saving…' : 'Save sites'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
